import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Store, Utensils, CreditCard, Banknote, Smartphone, Loader2, CheckCircle2, Navigation, AlertCircle, Mail, Upload, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import { DeliveryType, Restaurant, RestaurantSettings } from '../../lib/types';
import { cn, validateCaprichoDeliveryZone } from '../../lib/utils';
import { useTableOrder } from '../../lib/table-context';
import { isPreorderRestaurant, DELIVERY_TIME_SLOTS, getUpcomingSaturdays, formatDeliveryDate, toDateString, getOrderingWindowMessage } from '../../lib/preorder';
import { useCustomerLookup } from '../../lib/loyalty-hooks';
import { LoyaltyCard } from './LoyaltyCard';
import { useLanguage } from '../../lib/i18n';
import { getCustomerByPhone, getCustomerFromLocal } from '../../lib/customer';
import { supabase } from '../../lib/supabase';
import { uploadPaymentProofToCloudinary } from '../../lib/ticket';
// ─── Crack Pollo dynamic delivery zones ──────────────────────
const CRACK_POLLO_DELIVERY_ZONES: {
  level1: string;
  places: {
    name: string;
    fee: number;
  }[];
}[] = [{
  level1: 'San Pablo',
  places: [{
    name: 'Barrio El Estadio',
    fee: 700
  }, {
    name: 'Barrio Fátima',
    fee: 700
  }, {
    name: 'Barrio Juan Pablo II',
    fee: 700
  }, {
    name: 'Barrio Padre Pío',
    fee: 700
  }, {
    name: 'Bomba El Cruce',
    fee: 700
  }, {
    name: 'Calle Los Moreno',
    fee: 700
  }, {
    name: 'Cementerio San Pablo',
    fee: 700
  }, {
    name: 'La Clara',
    fee: 700
  }, {
    name: 'Los Barriles (Barrio Los Ángeles)',
    fee: 700
  }, {
    name: 'Oficina Volcafé',
    fee: 700
  }, {
    name: 'Rancho San Francisco',
    fee: 700
  }, {
    name: 'Barrio El Rosario',
    fee: 1000
  }, {
    name: 'Cabinas Coyote',
    fee: 1000
  }, {
    name: 'Cafetería Haug',
    fee: 1000
  }, {
    name: 'Coopeleco',
    fee: 1000
  }, {
    name: 'Donguis',
    fee: 1000
  }, {
    name: 'Granja Ecopollo',
    fee: 1000
  }, {
    name: 'Oficinas Ecopollo',
    fee: 1000
  }, {
    name: 'Socolilla',
    fee: 1000
  }, {
    name: 'Abejonal',
    fee: 1500
  }, {
    name: 'Altos de los Gamboa',
    fee: 1500
  }, {
    name: 'Carrizal',
    fee: 1500
  }, {
    name: 'Casquillo',
    fee: 1500
  }, {
    name: 'La Virgen',
    fee: 1500
  }, {
    name: 'Montes de Oro',
    fee: 1500
  }, {
    name: 'San Isidro',
    fee: 2500
  }, {
    name: 'San Antonio',
    fee: 3000
  }]
}, {
  level1: 'San Marcos',
  places: [{
    name: 'Barrio El Estadio San Marcos',
    fee: 1500
  }, {
    name: 'Barrio El Sitio',
    fee: 1500
  }, {
    name: 'Barrio La Clínica',
    fee: 1500
  }, {
    name: 'Barrio Los Navarro',
    fee: 1500
  }, {
    name: 'Calle Los Ulloa',
    fee: 1500
  }, {
    name: 'El Balahar',
    fee: 1500
  }, {
    name: 'La Placa',
    fee: 1500
  }, {
    name: 'La Recta',
    fee: 1500
  }, {
    name: 'Las 3 Marías',
    fee: 1500
  }, {
    name: 'San Marcos Centro',
    fee: 1500
  }, {
    name: 'Santa Cecilia',
    fee: 1500
  }, {
    name: 'Bajo San Juan',
    fee: 2000
  }, {
    name: 'El Rodeo',
    fee: 2000
  }, {
    name: 'La Sabana',
    fee: 2500
  }, {
    name: 'Guadalupe',
    fee: 3000
  }, {
    name: 'Llano de la Piedra',
    fee: 3000
  }, {
    name: 'San Lorenzo',
    fee: 3000
  }, {
    name: 'Canet',
    fee: 4000
  }, {
    name: 'Santa Marta',
    fee: 4000
  }]
}, {
  level1: 'Santa María',
  places: [{
    name: 'Santa María',
    fee: 4000
  }]
}];
function sortCrackPolloPlaces(places: {
  name: string;
  fee: number;
}[]): {
  name: string;
  fee: number;
}[] {
  return [...places].sort((a, b) => {
    if (a.fee !== b.fee) return a.fee - b.fee;
    return a.name.localeCompare(b.name, 'es');
  });
}
function formatCrackPolloFee(fee: number): string {
  return `₡${fee.toLocaleString('es-CR')}`;
}
// Crack Pollo: when the customer picks "Otro" inside a Level 1 zone, the
// express fee is not known yet — the restaurant will confirm it by WhatsApp.
const CRACK_POLLO_PENDING_FEE_LABEL = 'Cobro express pendiente de confirmar por WhatsApp';
interface CheckoutFormProps {
  restaurant: Restaurant | null;
  settings: RestaurantSettings | null;
  onSubmit: (data: CheckoutFormData) => void;
  isLoading: boolean;
  initialCustomer?: {
    customer_name: string;
    customer_phone: string;
    delivery_address: string;
    maps_url: string;
  } | null;
  onCrackPolloDeliveryFeeChange?: (fee: number) => void;
  // MS Express external delivery — emits the currently selected zone/subzone
  // and its fee (or null when nothing is selected) so CheckoutPage can keep
  // the food subtotal separate from the delivery fee. Only used when
  // settings.uses_external_delivery === true.
  onExternalDeliveryChange?: (info: {
    fee: number;
    zone: string;
    subzone: string;
    catalogId: string | null;
  } | null) => void;
  orderTotal?: number;
}
// ─── MS Express external delivery catalog row (flexible shape) ──
// The delivery_zones_catalog table already exists in the DB. We read it with
// select('*') and resolve the relevant fields tolerantly so we never break if
// the exact column names differ slightly across environments.
interface MsExpressZoneRow {
  id: string | null;
  zone: string;
  subzone: string;
  fee: number;
}
function resolveMsExpressRow(r: any): MsExpressZoneRow | null {
  const zone = String(r?.zone ?? r?.zone_name ?? r?.provincia ?? '').trim();
  const subzone = String(r?.subzone ?? r?.subzone_name ?? r?.localidad ?? r?.locality ?? '').trim();
  const feeRaw = r?.delivery_fee ?? r?.fee ?? r?.price ?? r?.cost ?? r?.precio ?? 0;
  const fee = Number(feeRaw) || 0;
  if (!zone || !subzone) return null;
  return {
    id: r?.id != null ? String(r.id) : null,
    zone,
    subzone,
    fee
  };
}
export interface CheckoutFormData {
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  delivery_type: DeliveryType;
  payment_method: string;
  delivery_address: string;
  maps_url: string;
  delivery_lat: number | null;
  delivery_lng: number | null;
  table_id: string;
  notes: string;
  preorder_date?: string;
  preorder_time_slot?: string;
  wants_invoice: boolean;
  invoice_customer_name?: string;
  invoice_legal_id?: string;
  invoice_email?: string;
  dynamic_delivery_fee?: number | null;
  delivery_zone_level1?: string;
  delivery_zone_place?: string;
  // MS Express external delivery (only set when uses_external_delivery=true)
  delivery_zone?: string;
  delivery_subzone?: string;
  delivery_zone_catalog_id?: string | null;
  external_delivery_fee?: number;
  // When the customer pays by SINPE for a delivery and uploads a proof,
  // we upload to Cloudinary and surface the URL here so the WhatsApp
  // message and order confirmation can reference it directly.
  payment_proof_url?: string | null;
}
export function CheckoutForm({
  restaurant,
  settings,
  onSubmit,
  isLoading,
  initialCustomer,
  onCrackPolloDeliveryFeeChange,
  onExternalDeliveryChange,
  orderTotal = 0
}: CheckoutFormProps) {
  const {
    tableOrder,
    isTableOrder
  } = useTableOrder();
  const {
    t
  } = useLanguage();
  // Simplified QR checkout (Name + Email only) is opt-in per restaurant.
  // Currently enabled for NatuCafé (id 19) and Rancho San Francisco (id 28).
  // Other restaurants always use the full marketplace checkout, even if a
  // stale QR table context exists in localStorage from a previous scan.
  const isQrTableForThisRestaurant = isTableOrder && !!tableOrder && !!restaurant?.id && String(tableOrder.restaurantId) === String(restaurant.id);
  const useSimplifiedQrCheckout = isQrTableForThisRestaurant && (Number(restaurant?.id) === 19 || Number(restaurant?.id) === 28);
  const isNatucafeMarketplace = Number(restaurant?.id) === 19 && !useSimplifiedQrCheckout;
  const isCabanaParrillera = Number(restaurant?.id) === 24 || restaurant?.slug === 'cabana-parrillera' || restaurant?.slug === 'la-cabana-parrillera';
  // Emilius Pizza — always offer the three fulfillment channels in checkout
  // (pasar a recoger / comer en el restaurante / express) regardless of the
  // restaurant settings toggles. Strictly additive, Emilius-scoped.
  const isEmilius = Number(restaurant?.id) === 27 || restaurant?.slug === 'emilius-pizza';
  // La Cabaña Parrillera — show a one-time popup when the customer picks
  // delivery ("express") so they understand the express fee is quoted
  // separately via WhatsApp and is NOT included in the cart total.
  const [showCabanaExpressNotice, setShowCabanaExpressNotice] = useState(false);
  const cabanaExpressNoticeShownRef = useRef(false);
  // Crack Pollo: dynamic delivery zone selector (3-step picker).
  // Only active for slug 'crack-pollo'. Does NOT affect other restaurants.
  const isCrackPollo = restaurant?.slug === 'crack-pollo';
  const [selectedDeliveryLevel1, setSelectedDeliveryLevel1] = useState<string>('');
  const [selectedDeliveryPlace, setSelectedDeliveryPlace] = useState<string>('');
  const [selectedDeliveryFee, setSelectedDeliveryFee] = useState<number>(0);
  const [deliveryZoneConfirmed, setDeliveryZoneConfirmed] = useState<boolean>(false);
  // Crack Pollo: "Otro" option inside each Level 1. Customer types a free-form
  // place name; the express fee stays at 0 and is shown as "pending".
  const [selectedDeliveryPlaceIsOther, setSelectedDeliveryPlaceIsOther] = useState<boolean>(false);
  const [otherPlaceText, setOtherPlaceText] = useState<string>('');
  // ─── MS Express external delivery ──────────────────────────
  // Gated entirely behind restaurant_settings.uses_external_delivery. When
  // the flag is false/null this whole block is inert and the existing flow
  // is unchanged.
  const usesExternalDelivery = settings?.uses_external_delivery === true;
  const [msZones, setMsZones] = useState<MsExpressZoneRow[]>([]);
  const [msZonesLoading, setMsZonesLoading] = useState(false);
  const [selectedMsZone, setSelectedMsZone] = useState<string>('');
  const [selectedMsSubzone, setSelectedMsSubzone] = useState<string>('');
  const [selectedMsFee, setSelectedMsFee] = useState<number>(0);
  const [selectedMsCatalogId, setSelectedMsCatalogId] = useState<string | null>(null);
  // Cabaña Parrillera: customer indicates arrival time when dining in
  const [dineInArrivalTime, setDineInArrivalTime] = useState<string>('');
  const [cashAmount, setCashAmount] = useState<string>('');
  const [formData, setFormData] = useState<CheckoutFormData>({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    delivery_type: useSimplifiedQrCheckout ? 'dine_in' : 'pickup',
    payment_method: 'sinpe',
    delivery_address: '',
    maps_url: '',
    delivery_lat: null,
    delivery_lng: null,
    table_id: useSimplifiedQrCheckout ? tableOrder?.tableId || '' : '',
    notes: '',
    wants_invoice: false,
    invoice_customer_name: '',
    invoice_legal_id: '',
    invoice_email: ''
  });
  // Check localStorage for returning customer on mount
  const localCustomer = getCustomerFromLocal();
  const [termsAccepted, setTermsAccepted] = useState(localCustomer?.terms_accepted ?? false);
  const [isReturningCustomer, setIsReturningCustomer] = useState(localCustomer?.terms_accepted ?? false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormData, string>>>({});
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [geoError, setGeoError] = useState('');
  // Check if returning customer (already accepted terms) on phone change
  useEffect(() => {
    const checkReturningCustomer = async () => {
      const phone = formData.customer_phone.replace(/\D/g, '');
      if (phone.length < 8) {
        setIsReturningCustomer(false);
        return;
      }
      try {
        const cust = await getCustomerByPhone(phone);
        if (cust && cust.terms_accepted_at) {
          setIsReturningCustomer(true);
          setTermsAccepted(true);
          setFormData(prev => ({
            ...prev,
            customer_name: prev.customer_name || cust.full_name || '',
            customer_email: prev.customer_email || cust.email || '',
            delivery_address: prev.delivery_address || cust.address_text || '',
            maps_url: prev.maps_url || cust.maps_url || ''
          }));
          return;
        } else if (cust) {
          setFormData(prev => ({
            ...prev,
            customer_name: prev.customer_name || cust.full_name || '',
            customer_email: prev.customer_email || cust.email || '',
            delivery_address: prev.delivery_address || cust.address_text || '',
            maps_url: prev.maps_url || cust.maps_url || ''
          }));
          return;
        }
      } catch (err) {
        console.error('[CHECKOUT] checkReturningCustomer Supabase error:', err);
      }
      // Fallback: check localStorage
      const local = getCustomerFromLocal();
      if (local && local.phone === phone) {
        if (local.terms_accepted) {
          setIsReturningCustomer(true);
          setTermsAccepted(true);
        }
        setFormData(prev => ({
          ...prev,
          customer_name: prev.customer_name || local.full_name || '',
          customer_email: prev.customer_email || local.email || '',
          delivery_address: prev.delivery_address || local.address_text || '',
          maps_url: prev.maps_url || local.maps_url || ''
        }));
      }
    };
    const debounce = setTimeout(checkReturningCustomer, 500);
    return () => clearTimeout(debounce);
  }, [formData.customer_phone]);
  // Apply initialCustomer data from localStorage lookup
  useEffect(() => {
    if (initialCustomer) {
      setFormData(prev => ({
        ...prev,
        customer_name: initialCustomer.customer_name || prev.customer_name,
        customer_phone: initialCustomer.customer_phone || prev.customer_phone,
        delivery_address: initialCustomer.delivery_address || prev.delivery_address,
        maps_url: initialCustomer.maps_url || prev.maps_url
      }));
    }
  }, [initialCustomer]);
  const isPreorder = isPreorderRestaurant(restaurant?.id);
  const preorderSaturdays = isPreorder ? getUpcomingSaturdays(3) : [];
  const preorderWindow = isPreorder ? getOrderingWindowMessage() : null;
  // Loyalty Integration
  const {
    customer,
    loading: customerLoading
  } = useCustomerLookup(formData.customer_phone, restaurant?.id);
  // Autofill when customer is found
  useEffect(() => {
    if (customer && !formData.customer_name) {
      setFormData(prev => ({
        ...prev,
        customer_name: customer.full_name || prev.customer_name
      }));
    }
  }, [customer]);
  // Autofill address when found
  useEffect(() => {
    if (customer?.address_text && !formData.delivery_address && formData.delivery_type === 'delivery') {
      setFormData(prev => ({
        ...prev,
        delivery_address: customer.address_text || prev.delivery_address,
        maps_url: customer.maps_url || prev.maps_url
      }));
    }
  }, [customer, formData.delivery_type]);
  // Initialize default delivery type based on settings
  useEffect(() => {
    if (settings) {
      if (settings.accept_delivery) setFormData(prev => ({
        ...prev,
        delivery_type: 'delivery'
      }));else if (settings.accept_pickup) setFormData(prev => ({
        ...prev,
        delivery_type: 'pickup'
      }));
    }
  }, [settings]);
  const handleDeliveryTypeChange = (type: DeliveryType) => {
    setFormData(prev => ({
      ...prev,
      delivery_type: type
    }));
  };
  // MS Express: load the external delivery zones once, only when the feature
  // flag is on. Filtered to provider_name='MS Express', restaurant_id IS NULL
  // (shared catalog) and is_active=true.
  useEffect(() => {
    if (!usesExternalDelivery) return;
    let cancelled = false;
    const loadZones = async () => {
      setMsZonesLoading(true);
      try {
        const {
          data,
          error
        } = await supabase.from('delivery_zones_catalog').select('id, zone, subzone, price').eq('provider_name', 'MS Express').is('restaurant_id', null).eq('is_active', true).order('zone', {
          ascending: true
        }).order('price', {
          ascending: true
        }).order('subzone', {
          ascending: true
        });
        if (error) {
          console.error('[MS_EXPRESS] delivery_zones_catalog error:', error);
          if (!cancelled) setMsZones([]);
          return;
        }
        const rows = (data || []).map(resolveMsExpressRow).filter((r): r is MsExpressZoneRow => r !== null);
        if (!cancelled) setMsZones(rows);
      } catch (err) {
        console.error('[MS_EXPRESS] load zones exception:', err);
        if (!cancelled) setMsZones([]);
      } finally {
        if (!cancelled) setMsZonesLoading(false);
      }
    };
    loadZones();
    return () => {
      cancelled = true;
    };
  }, [usesExternalDelivery]);
  // MS Express: reset the selection whenever the customer leaves delivery.
  useEffect(() => {
    if (!usesExternalDelivery) return;
    if (formData.delivery_type !== 'delivery') {
      setSelectedMsZone('');
      setSelectedMsSubzone('');
      setSelectedMsFee(0);
      setSelectedMsCatalogId(null);
    }
  }, [usesExternalDelivery, formData.delivery_type]);
  // MS Express: notify CheckoutPage of the current selection + fee so the
  // order summary (food subtotal / delivery / total) stays in sync.
  useEffect(() => {
    if (!usesExternalDelivery) return;
    if (!onExternalDeliveryChange) return;
    if (formData.delivery_type === 'delivery' && selectedMsZone && selectedMsSubzone) {
      onExternalDeliveryChange({
        fee: selectedMsFee,
        zone: selectedMsZone,
        subzone: selectedMsSubzone,
        catalogId: selectedMsCatalogId
      });
    } else {
      onExternalDeliveryChange(null);
    }
  }, [usesExternalDelivery, formData.delivery_type, selectedMsZone, selectedMsSubzone, selectedMsFee, selectedMsCatalogId, onExternalDeliveryChange]);
  // MS Express: derived helpers for the selector
  const msZoneNames = Array.from(new Set(msZones.map(z => z.zone))).sort((a, b) => a.localeCompare(b, 'es'));
  const msSubzonesForZone = msZones.filter(z => z.zone === selectedMsZone).sort((a, b) => {
    if (a.fee !== b.fee) return a.fee - b.fee;
    return a.subzone.localeCompare(b.subzone, 'es');
  });
  // La Cabaña Parrillera — fire the express-fee notice once per checkout
  // session, the first time the customer selects delivery.
  useEffect(() => {
    if (!isCabanaParrillera) return;
    if (formData.delivery_type !== 'delivery') return;
    if (cabanaExpressNoticeShownRef.current) return;
    cabanaExpressNoticeShownRef.current = true;
    setShowCabanaExpressNotice(true);
  }, [isCabanaParrillera, formData.delivery_type]);
  const [sinpeProofFile, setSinpeProofFile] = useState<File | null>(null);
  const [sinpeProofPreview, setSinpeProofPreview] = useState<string>('');
  const [uploadingProof, setUploadingProof] = useState(false);
  // NatuCafé marketplace: el método de pago ya NO se fuerza a SINPE.
  // El cliente puede elegir entre SINPE, Efectivo o Tarjeta (estos dos
  // últimos se pagan contra entrega con el mensajero). El default inicial
  // sigue siendo 'sinpe' (ver estado inicial de formData).
  // Crack Pollo: reset zone state whenever the customer switches away from delivery.
  // This guarantees the express fee returns to 0 for pickup/dine_in.
  useEffect(() => {
    if (!isCrackPollo) return;
    if (formData.delivery_type !== 'delivery') {
      setSelectedDeliveryLevel1('');
      setSelectedDeliveryPlace('');
      setSelectedDeliveryFee(0);
      setDeliveryZoneConfirmed(false);
      setSelectedDeliveryPlaceIsOther(false);
      setOtherPlaceText('');
    }
  }, [isCrackPollo, formData.delivery_type]);
  // Crack Pollo: if the customer switches to delivery while having card selected,
  // clear the payment method since card is not allowed for express delivery.
  useEffect(() => {
    if (!isCrackPollo) return;
    if (formData.delivery_type === 'delivery' && formData.payment_method === 'card') {
      setFormData(prev => ({
        ...prev,
        payment_method: 'cash'
      }));
    }
  }, [isCrackPollo, formData.delivery_type, formData.payment_method]);
  // Crack Pollo: notify parent (CheckoutPage) of the current effective fee
  // so the order summary sidebar and totals stay in sync in real time.
  useEffect(() => {
    if (!isCrackPollo) return;
    if (!onCrackPolloDeliveryFeeChange) return;
    const effective = formData.delivery_type === 'delivery' && deliveryZoneConfirmed ? selectedDeliveryFee : 0;
    onCrackPolloDeliveryFeeChange(effective);
  }, [isCrackPollo, formData.delivery_type, deliveryZoneConfirmed, selectedDeliveryFee, onCrackPolloDeliveryFeeChange]);
  const validate = () => {
    const newErrors: Partial<Record<keyof CheckoutFormData, string>> = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'El nombre es requerido';
    // Phone is optional only in the simplified NatuCafé QR flow.
    // Every other checkout (marketplace, other restaurants) requires it.
    if (!useSimplifiedQrCheckout) {
      if (!formData.customer_phone.trim()) newErrors.customer_phone = 'El teléfono es requerido';else if (formData.customer_phone.replace(/\D/g, '').length < 8) {
        newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
      }
    } else if (formData.customer_phone.trim() && formData.customer_phone.replace(/\D/g, '').length < 8) {
      newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
    }
    if (formData.delivery_type === 'delivery') {
      if (!formData.delivery_address.trim()) newErrors.delivery_address = 'La dirección es requerida para delivery';
      // GPS is mandatory for delivery
      if (formData.delivery_lat === null || formData.delivery_lng === null) {
        ;
        (newErrors as any).maps_url = 'La ubicación GPS es obligatoria para delivery';
      }
      // Crack Pollo: zone must be confirmed before continuing.
      if (isCrackPollo && !deliveryZoneConfirmed) {
        ;
        (newErrors as any).delivery_zone = 'Selecciona y confirma tu zona de entrega';
      }
      // MS Express: zone + subzone are mandatory before continuing.
      if (usesExternalDelivery) {
        if (!selectedMsZone) {
          ;
          (newErrors as any).ms_zone = 'Selecciona tu zona de entrega';
        } else if (!selectedMsSubzone) {
          ;
          (newErrors as any).ms_subzone = 'Selecciona tu localidad';
        }
      }
      // Capricho de Canela: validate delivery zone (5km radius)
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho && formData.delivery_lat !== null && formData.delivery_lng !== null) {
        const zoneCheck = validateCaprichoDeliveryZone(formData.delivery_lat, formData.delivery_lng);
        if (!zoneCheck.valid) {
          newErrors.delivery_address = `Lo sentimos, solo hacemos delivery en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Tu ubicación está a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`;
        }
      }
    }
    if (formData.delivery_type === 'dine_in' && !useSimplifiedQrCheckout) {
      // Marketplace dine-in (any restaurant): require arrival time so the
      // kitchen has the order ready when the customer arrives.
      if (!dineInArrivalTime.trim()) (newErrors as any).dine_in_arrival_time = 'Indica a qué hora llegarás';
    }
    if (formData.wants_invoice) {
      if (!formData.invoice_customer_name?.trim()) (newErrors as any).invoice_customer_name = 'El nombre o razón social es requerido';
      if (!formData.invoice_email?.trim()) (newErrors as any).invoice_email = 'El correo es requerido para enviar la factura';else if (!/^\S+@\S+\.\S+$/.test(formData.invoice_email)) {
        ;
        (newErrors as any).invoice_email = 'Ingresa un correo válido';
      }
    }
    if (isPreorder) {
      if (!formData.preorder_date) (newErrors as any).preorder_date = 'Selecciona una fecha de entrega';
      if (!formData.preorder_time_slot) (newErrors as any).preorder_time_slot = 'Selecciona un horario de entrega';
    }
    // SINPE proof required for any restaurant when payment method is SINPE
    // (NatuCafé marketplace, Cabaña Parrillera, etc).
    const requiresSinpeProof = !useSimplifiedQrCheckout && formData.payment_method === 'sinpe';
    if (requiresSinpeProof && !sinpeProofFile) {
      ;
      (newErrors as any).sinpe_proof = 'Debes adjuntar la captura del pago SINPE para continuar';
    }
    setErrors(newErrors);
    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${firstErrorKey}"]`);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 50);
    }
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    let finalFormData = {
      ...formData
    };
    // Crack Pollo: inject delivery zone info into notes and dynamic fee field
    // so CheckoutPage uses the per-place fee and the WhatsApp / kitchen ticket
    // both show the exact zone the customer selected.
    if (isCrackPollo && formData.delivery_type === 'delivery' && deliveryZoneConfirmed && selectedDeliveryLevel1 && selectedDeliveryPlace) {
      const zoneLines = [`📍 Zona principal: ${selectedDeliveryLevel1}`, `📍 Lugar: ${selectedDeliveryPlace}`, `🚚 Tarifa express: ${selectedDeliveryPlaceIsOther ? CRACK_POLLO_PENDING_FEE_LABEL : formatCrackPolloFee(selectedDeliveryFee)}`].join('\n');
      finalFormData = {
        ...finalFormData,
        notes: finalFormData.notes ? `${zoneLines}\n${finalFormData.notes}` : zoneLines,
        dynamic_delivery_fee: selectedDeliveryFee,
        delivery_zone_level1: selectedDeliveryLevel1,
        delivery_zone_place: selectedDeliveryPlace
      };
    }
    // MS Express: attach the chosen zone/subzone/fee so CheckoutPage and the
    // create-order payload keep the delivery fee separate from the food
    // subtotal. Only runs behind the uses_external_delivery flag.
    if (usesExternalDelivery && formData.delivery_type === 'delivery' && selectedMsZone && selectedMsSubzone) {
      finalFormData = {
        ...finalFormData,
        delivery_zone: selectedMsZone,
        delivery_subzone: selectedMsSubzone,
        delivery_zone_catalog_id: selectedMsCatalogId,
        external_delivery_fee: selectedMsFee
      };
    }
    // Crack Pollo & Cabaña Parrillera (marketplace): inject cash amount and
    // change into notes so the kitchen knows how much to give back.
    if ((isCrackPollo || isCabanaParrillera && !isQrTableForThisRestaurant) && formData.payment_method === 'cash' && cashAmount) {
      const cashVal = Number(cashAmount);
      const changeVal = cashVal >= orderTotal ? cashVal - orderTotal : 0;
      const cashLines = `💵 Paga con: ₡${cashVal.toLocaleString('es-CR')} — Vuelto: ₡${changeVal.toLocaleString('es-CR')}`;
      finalFormData = {
        ...finalFormData,
        notes: finalFormData.notes ? `${cashLines}\n${finalFormData.notes}` : cashLines
      };
    }
    // Marketplace dine-in (any restaurant): prepend arrival time to notes so
    // it appears on the kitchen ticket / order detail clearly.
    if (!useSimplifiedQrCheckout && formData.delivery_type === 'dine_in' && dineInArrivalTime.trim()) {
      const arrivalLine = `🕐 Hora de llegada: ${dineInArrivalTime.trim()}`;
      finalFormData = {
        ...finalFormData,
        notes: finalFormData.notes ? `${arrivalLine}\n${finalFormData.notes}` : arrivalLine
      };
    }
    // SINPE proof upload — for any restaurant accepting SINPE.
    // NEW: when this is a Delivery + SINPE order we upload to Cloudinary
    // so the WhatsApp message can attach the public secure_url directly
    // (replacing the tracking link). For every other SINPE flow (pickup,
    // dine-in, NatuCafé marketplace) we keep the existing Supabase
    // Storage upload exactly as it was to avoid any regression.
    const shouldUploadProof = !useSimplifiedQrCheckout && formData.payment_method === 'sinpe';
    if (shouldUploadProof && sinpeProofFile) {
      const isDeliverySinpe = formData.payment_method === 'sinpe' && formData.delivery_type === 'delivery';
      if (isDeliverySinpe) {
        try {
          setUploadingProof(true);
          const cloudUrl = await uploadPaymentProofToCloudinary(sinpeProofFile, restaurant?.slug || `restaurant-${restaurant?.id ?? 'unknown'}`, `${restaurant?.slug || 'order'}-${Date.now()}-sinpe`);
          if (!cloudUrl) {
            setErrors(prev => ({
              ...(prev as any),
              sinpe_proof: 'No pudimos subir el comprobante. Intentá nuevamente.'
            }));
            setUploadingProof(false);
            return;
          }
          // NOTE: We intentionally do NOT inject the proof URL into notes
          // here. The WhatsApp message renders a dedicated "COMPROBANTE DE
          // PAGO" block from payment_proof_url, so duplicating it inside
          // notes would show the link twice in the WhatsApp output.
          finalFormData = {
            ...finalFormData,
            payment_proof_url: cloudUrl
          };
          setUploadingProof(false);
        } catch (err: any) {
          setErrors(prev => ({
            ...(prev as any),
            sinpe_proof: 'No pudimos subir el comprobante. Intentá nuevamente.'
          }));
          setUploadingProof(false);
          return;
        }
      } else {
        try {
          setUploadingProof(true);
          const ext = sinpeProofFile.name.split('.').pop() || 'jpg';
          const safeName = (formData.customer_name || 'cliente').toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'cliente';
          const restaurantFolder = `restaurant-${restaurant?.id ?? 'unknown'}`;
          const path = `sinpe-proofs/${restaurantFolder}/${Date.now()}-${safeName}.${ext}`;
          const {
            error: uploadError
          } = await supabase.storage.from('menu-images').upload(path, sinpeProofFile, {
            upsert: true,
            contentType: sinpeProofFile.type
          });
          if (uploadError) {
            setErrors(prev => ({
              ...(prev as any),
              sinpe_proof: 'No pudimos subir tu captura. Verificá tu conexión e intentá de nuevo.'
            }));
            setUploadingProof(false);
            return;
          }
          const {
            data: urlData
          } = supabase.storage.from('menu-images').getPublicUrl(path);
          const proofUrl = urlData.publicUrl;
          const proofLine = `Comprobante SINPE: ${proofUrl}`;
          finalFormData = {
            ...finalFormData,
            notes: finalFormData.notes ? `${finalFormData.notes}\n${proofLine}` : proofLine
          };
          setUploadingProof(false);
        } catch (err: any) {
          setErrors(prev => ({
            ...(prev as any),
            sinpe_proof: 'No pudimos subir tu captura. Verificá tu conexión e intentá de nuevo.'
          }));
          setUploadingProof(false);
          return;
        }
      }
    }
    onSubmit(finalFormData);
  };
  const handleChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('error');
      setGeoError('Tu navegador no soporta geolocalización');
      return;
    }
    setGeoStatus('loading');
    setGeoError('');
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      // Capricho de Canela: validate delivery zone immediately
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho) {
        const zoneCheck = validateCaprichoDeliveryZone(latitude, longitude);
        if (!zoneCheck.valid) {
          setGeoStatus('error');
          setGeoError(`Tu ubicación está fuera de nuestra zona de delivery. Solo entregamos en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Estás a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`);
          return;
        }
      }
      setFormData(prev => ({
        ...prev,
        delivery_lat: latitude,
        delivery_lng: longitude,
        maps_url: `https://maps.google.com/?q=${latitude},${longitude}`
      }));
      setGeoStatus('success');
    }, error => {
      setGeoStatus('error');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGeoError('Permiso denegado. Por favor, permite el acceso a tu ubicación.');
          break;
        case error.POSITION_UNAVAILABLE:
          setGeoError('La información de ubicación no está disponible.');
          break;
        case error.TIMEOUT:
          setGeoError('Se agotó el tiempo de espera para obtener la ubicación.');
          break;
        default:
          setGeoError('Ocurrió un error desconocido al obtener la ubicación.');
          break;
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  // Determine available payment methods from restaurant
  const showSinpe = restaurant?.accepts_sinpe ?? true;
  const showCash = restaurant?.accepts_cash ?? true;
  const showCard = restaurant?.accepts_card ?? true;
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-1504">
      {/* QR Table Banner — only for the simplified NatuCafé QR flow */}
      {useSimplifiedQrCheckout && tableOrder && <section className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200 flex items-center gap-4" data-id="element-1505">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0" data-id="element-1506">
            <Utensils className="w-6 h-6 text-emerald-600" data-id="element-1507" />
          </div>
          <div data-id="element-1508">
            <p className="font-bold text-emerald-900 text-lg" data-id="element-1509">
              {tableOrder.tableName} · Autoatención
            </p>
            <p className="text-sm text-emerald-700" data-id="element-1510">
              Tu pedido se enviará directamente a esta mesa
            </p>
          </div>
        </section>}

      {/* Contact Info */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1511">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-1512">
          <Store className="w-6 h-6 text-brand-primary" data-id="element-1513" />
          {t('checkout_title')}
        </h2>

        <div className="space-y-4" data-id="element-1514">
          <div data-field="customer_name" data-id="element-1515">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1516">
              {t('checkout_name')}
              {errors.customer_name && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1517" />}
            </label>
            <Input value={formData.customer_name} onChange={e => handleChange('customer_name', e.target.value)} error={errors.customer_name} required data-id="element-1518" />
          </div>

          {!useSimplifiedQrCheckout && <div data-field="customer_phone" data-id="element-1519">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1520">
                {t('checkout_phone')}
                {errors.customer_phone && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1521" />}
              </label>
              <div className="relative" data-id="element-1522">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-1523">
                  📞
                </span>
                <Input type="tel" placeholder="88888888" value={formData.customer_phone} onChange={e => setFormData({
              ...formData,
              customer_phone: e.target.value
            })} error={errors.customer_phone} required data-id="element-1524" />
              </div>
            </div>}

          <div data-field="customer_email" data-id="element-1525">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1526">
              {t('checkout_email')}
              {errors.customer_email && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1527" />}
            </label>
            <Input value={formData.customer_email} onChange={e => handleChange('customer_email', e.target.value)} error={errors.customer_email} placeholder="email@ejemplo.com" data-id="element-1528" />
          </div>

          {!useSimplifiedQrCheckout && <LoyaltyCard phone={formData.customer_phone} restaurantId={restaurant?.id} data-id="element-1529" />}
        </div>
      </div>

      {/* Preorder Schedule — for preorder restaurants */}
      {isPreorder && preorderWindow && <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-1530">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1531">
            📦 Entrega Programada
          </h3>

          {/* Schedule Info */}
          <div className={cn('rounded-xl p-4 border mb-5', preorderWindow.isOpen ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200')} data-id="element-1532">
            <p className={cn('font-bold text-sm mb-1', preorderWindow.isOpen ? 'text-emerald-900' : 'text-red-900')} data-id="element-1533">
              {preorderWindow.title}
            </p>
            <p className={cn('text-xs', preorderWindow.isOpen ? 'text-emerald-700' : 'text-red-700')} data-id="element-1534">
              {preorderWindow.message}
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500" data-id="element-1535">
              <span data-id="element-1536">📅 Pedidos: Lun–Vie</span>
              <span data-id="element-1537">·</span>
              <span data-id="element-1538">🚚 Entregas: Sábado 8am–4pm</span>
            </div>
          </div>

          {/* Date Selection */}
          <div className="mb-5" data-id="element-1539">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1540">
              Fecha de entrega (sábado)
            </label>
            <div className="space-y-2" data-id="element-1541">
              {preorderSaturdays.map(sat => {
            const dateStr = toDateString(sat);
            const isSelected = formData.preorder_date === dateStr;
            return <button key={dateStr} type="button" onClick={() => handleChange('preorder_date' as any, dateStr)} className={cn('w-full p-3.5 rounded-xl border-2 text-sm font-semibold transition-all text-left', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-1542">
                    📅 {formatDeliveryDate(sat)}
                  </button>;
          })}
            </div>
            {(errors as any).preorder_date && <p className="text-sm text-red-500 mt-1" data-id="element-1543">
                {(errors as any).preorder_date}
              </p>}
          </div>

          {/* Time Slot Selection */}
          {formData.preorder_date && <div data-id="element-1544">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1545">
                Horario de entrega
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-1546">
                {DELIVERY_TIME_SLOTS.map(slot => {
            const isSelected = formData.preorder_time_slot === slot.id;
            return <button key={slot.id} type="button" onClick={() => handleChange('preorder_time_slot' as any, slot.id)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-1547">
                      🕐 {slot.label}
                    </button>;
          })}
              </div>
              {(errors as any).preorder_time_slot && <p className="text-sm text-red-500 mt-1" data-id="element-1548">
                  {(errors as any).preorder_time_slot}
                </p>}
            </div>}
        </section>}

      {/* Delivery Type — hidden only for the simplified NatuCafé QR flow */}
      {!useSimplifiedQrCheckout && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1549">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1550">
            <MapPin className="w-5 h-5 text-brand-primary" data-id="element-1551" />
            {t('checkout_delivery_type')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1552">
            {(settings?.accept_delivery || isEmilius) && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'delivery'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'delivery' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1553">
                {formData.delivery_type === 'delivery' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1554" />}
                <Navigation className="w-6 h-6 mb-2" data-id="element-1555" />
                <span className="font-semibold" data-id="element-1556">{t('delivery')}</span>
              </button>}

            {(settings?.accept_pickup || isEmilius) && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'pickup'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'pickup' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1557">
                {formData.delivery_type === 'pickup' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1558" />}
                <Store className="w-6 h-6 mb-2" data-id="element-1559" />
                <span className="font-semibold" data-id="element-1560">{t('pickup')}</span>
              </button>}

            {/* Comer en el local — always available for marketplace orders */}
            <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'dine_in'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'dine_in' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1561">
              {formData.delivery_type === 'dine_in' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1562" />}
              <Utensils className="w-6 h-6 mb-2" data-id="element-1563" />
              <span className="font-semibold" data-id="element-1564">Comer en el local</span>
            </button>
          </div>

          {/* Conditional Fields based on Delivery Type */}
          <div className="mt-6 space-y-4" data-id="element-1565">
            {formData.delivery_type === 'delivery' && <div className="mt-6 space-y-4" data-id="element-1566">
                {/* Crack Pollo: 3-step zone selector — only shown for slug 'crack-pollo' */}
                {isCrackPollo && <div data-field="delivery_zone" className="rounded-2xl border-2 border-brand-amber/40 bg-amber-50/40 p-4 sm:p-5" data-id="element-1567">
                    {/* Step 1: pick Level 1 */}
                    {!selectedDeliveryLevel1 && !deliveryZoneConfirmed && <>
                        <h4 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-1568">
                          ¿Para dónde va tu pedido?
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 mb-4" data-id="element-1569">
                          Primero elegí la zona principal.
                        </p>
                        <div className="space-y-2.5" data-id="element-1570">
                          {CRACK_POLLO_DELIVERY_ZONES.map(z => <button key={z.level1} type="button" onClick={() => {
                  setSelectedDeliveryLevel1(z.level1);
                  setSelectedDeliveryPlace('');
                  setSelectedDeliveryFee(0);
                  setDeliveryZoneConfirmed(false);
                  setSelectedDeliveryPlaceIsOther(false);
                  setOtherPlaceText('');
                  if ((errors as any).delivery_zone) {
                    setErrors(prev => ({
                      ...(prev as any),
                      delivery_zone: undefined
                    }));
                  }
                }} className="w-full flex items-center justify-between gap-3 px-4 py-4 rounded-xl border-2 border-gray-200 bg-white text-left hover:border-brand-amber hover:bg-amber-50 transition-colors" data-id="element-1571">
                              <span className="font-bold text-gray-900 text-base" data-id="element-1572">
                                {z.level1}
                              </span>
                              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" data-id="element-1573" />
                            </button>)}
                        </div>
                        {(errors as any).delivery_zone && <p className="text-sm text-red-500 mt-3 flex items-center gap-1.5" data-id="element-1574">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1575" />
                            {(errors as any).delivery_zone}
                          </p>}
                      </>}

                    {/* Step 2: pick Level 2 place within selected Level 1 */}
                    {selectedDeliveryLevel1 && !deliveryZoneConfirmed && <>
                        <div className="flex items-center justify-between gap-3 mb-3" data-id="element-1576">
                          <button type="button" onClick={() => {
                  setSelectedDeliveryLevel1('');
                  setSelectedDeliveryPlace('');
                  setSelectedDeliveryFee(0);
                  setSelectedDeliveryPlaceIsOther(false);
                  setOtherPlaceText('');
                }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900" data-id="element-1577">
                            <ChevronLeft className="w-4 h-4" data-id="element-1578" />
                            Volver
                          </button>
                          <span className="text-xs font-bold text-brand-amber uppercase tracking-wider" data-id="element-1579">
                            {selectedDeliveryLevel1}
                          </span>
                        </div>
                        <h4 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-1580">
                          Elegí tu lugar exacto
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 mb-4" data-id="element-1581">
                          Las tarifas se ordenan de menor a mayor.
                        </p>
                        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-id="element-1582">
                          {sortCrackPolloPlaces(CRACK_POLLO_DELIVERY_ZONES.find(z => z.level1 === selectedDeliveryLevel1)?.places || []).map(p => {
                  const isSelected = !selectedDeliveryPlaceIsOther && selectedDeliveryPlace === p.name;
                  return <button key={p.name} type="button" onClick={() => {
                    setSelectedDeliveryPlace(p.name);
                    setSelectedDeliveryFee(p.fee);
                    setSelectedDeliveryPlaceIsOther(false);
                    setOtherPlaceText('');
                  }} className={cn('w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-colors', isSelected ? 'border-brand-amber bg-amber-50 ring-2 ring-brand-amber/20' : 'border-gray-200 bg-white hover:border-brand-amber/60 hover:bg-amber-50/50')} data-id="element-1583">
                                <div className="min-w-0" data-id="element-1584">
                                  <div className="font-bold text-gray-900 text-sm sm:text-base truncate" data-id="element-1585">
                                    {p.name}
                                  </div>
                                  <div className="text-xs sm:text-sm text-gray-600 mt-0.5" data-id="element-1586">
                                    Express:{' '}
                                    <span className="font-bold text-brand-amber" data-id="element-1587">
                                      {formatCrackPolloFee(p.fee)}
                                    </span>
                                  </div>
                                </div>
                                {isSelected && <CheckCircle2 className="w-5 h-5 text-brand-amber shrink-0" data-id="element-1588" />}
                              </button>;
                })}

                          {/* "Otro:" option — customer types free-form place */}
                          <button type="button" onClick={() => {
                  setSelectedDeliveryPlaceIsOther(true);
                  setSelectedDeliveryPlace('');
                  setSelectedDeliveryFee(0);
                }} className={cn('w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border-2 border-dashed text-left transition-colors', selectedDeliveryPlaceIsOther ? 'border-brand-amber bg-amber-50 ring-2 ring-brand-amber/20' : 'border-gray-300 bg-white hover:border-brand-amber/60 hover:bg-amber-50/50')} data-id="element-1589">
                            <div className="min-w-0" data-id="element-1590">
                              <div className="font-bold text-gray-900 text-sm sm:text-base" data-id="element-1591">
                                Otro:
                              </div>
                              <div className="text-xs sm:text-sm text-gray-600 mt-0.5" data-id="element-1592">
                                Mi lugar no está en la lista
                              </div>
                            </div>
                            {selectedDeliveryPlaceIsOther && <CheckCircle2 className="w-5 h-5 text-brand-amber shrink-0" data-id="element-1593" />}
                          </button>
                        </div>

                        {/* Free-form "Otro" input — rendered OUTSIDE the
                            scroll container so it's always visible when
                            the customer taps the Otro card. */}
                        {selectedDeliveryPlaceIsOther && <div className="mt-3 rounded-xl border-2 border-brand-amber bg-white p-4 space-y-2" data-id="element-1594">
                            <label className="block text-sm font-bold text-gray-900" data-id="element-1595">
                              ¿Dónde va el pedido?{' '}
                              <span className="text-red-500" data-id="element-1596">*</span>
                            </label>
                            <input type="text" autoFocus value={otherPlaceText} onChange={e => setOtherPlaceText(e.target.value)} placeholder="Ej: Frente a la pulpería..." className="w-full h-12 px-3 rounded-lg border-2 border-gray-200 text-base focus:outline-none focus:border-brand-amber bg-white" data-id="element-1597" />
                            <p className="text-xs font-bold text-brand-amber" data-id="element-1598">
                              {CRACK_POLLO_PENDING_FEE_LABEL}
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-1599">
                              💡 Acordate de completar también la{' '}
                              <span className="font-semibold" data-id="element-1600">
                                Dirección exacta
                              </span>{' '}
                              y la{' '}
                              <span className="font-semibold" data-id="element-1601">
                                Ubicación GPS
                              </span>{' '}
                              abajo.
                            </p>
                          </div>}
                        <button type="button" disabled={selectedDeliveryPlaceIsOther ? !otherPlaceText.trim() : !selectedDeliveryPlace} onClick={() => {
                if (selectedDeliveryPlaceIsOther) {
                  const txt = otherPlaceText.trim();
                  if (!txt) return;
                  setSelectedDeliveryPlace(`Otro: ${txt}`);
                  setSelectedDeliveryFee(0);
                } else if (!selectedDeliveryPlace) {
                  return;
                }
                setDeliveryZoneConfirmed(true);
                if ((errors as any).delivery_zone) {
                  setErrors(prev => ({
                    ...(prev as any),
                    delivery_zone: undefined
                  }));
                }
              }} className={cn('mt-4 w-full h-12 rounded-xl font-bold text-base transition-colors', (selectedDeliveryPlaceIsOther ? otherPlaceText.trim() : selectedDeliveryPlace) ? 'bg-brand-amber text-white hover:bg-brand-amber-hover' : 'bg-gray-200 text-gray-400 cursor-not-allowed')} data-id="element-1602">
                          Confirmar ubicación
                        </button>
                      </>}

                    {/* Step 3: confirmed */}
                    {deliveryZoneConfirmed && <div className="rounded-xl bg-white border-2 border-emerald-200 p-4" data-id="element-1603">
                        <div className="flex items-center gap-2 mb-3" data-id="element-1604">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-1605" />
                          <span className="font-bold text-emerald-700" data-id="element-1606">
                            Tu entrega
                          </span>
                        </div>
                        <dl className="space-y-1.5 text-sm" data-id="element-1607">
                          <div className="flex justify-between gap-3" data-id="element-1608">
                            <dt className="text-gray-500" data-id="element-1609">Zona principal</dt>
                            <dd className="font-semibold text-gray-900 text-right" data-id="element-1610">
                              {selectedDeliveryLevel1}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3" data-id="element-1611">
                            <dt className="text-gray-500" data-id="element-1612">Lugar</dt>
                            <dd className="font-semibold text-gray-900 text-right" data-id="element-1613">
                              {selectedDeliveryPlace}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3" data-id="element-1614">
                            <dt className="text-gray-500" data-id="element-1615">Express</dt>
                            <dd className="font-bold text-brand-amber text-right" data-id="element-1616">
                              {selectedDeliveryPlaceIsOther ? CRACK_POLLO_PENDING_FEE_LABEL : formatCrackPolloFee(selectedDeliveryFee)}
                            </dd>
                          </div>
                        </dl>
                        <button type="button" onClick={() => {
                setDeliveryZoneConfirmed(false);
              }} className="mt-3 w-full h-10 rounded-lg border-2 border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1617">
                          Cambiar zona
                        </button>
                      </div>}
                  </div>}

                {/* MS Express external delivery selector — only when the
                    restaurant uses external delivery and delivery is chosen */}
                {usesExternalDelivery && <div data-field="ms_zone" className="rounded-2xl border-2 border-brand-amber/40 bg-amber-50/40 p-4 sm:p-5 space-y-4" data-id="element-1618">
                    <div data-id="element-1619">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-1620">
                        <Navigation className="w-5 h-5 text-brand-amber" data-id="element-1621" />
                        Delivery MS Express
                      </h4>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-1622">
                        Elegí tu zona y localidad para calcular el costo de
                        envío.
                      </p>
                    </div>

                    {msZonesLoading ? <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1623">
                        <Loader2 className="w-4 h-4 animate-spin" data-id="element-1624" />
                        Cargando zonas de entrega...
                      </div> : msZones.length === 0 ? <p className="text-sm text-red-600 flex items-center gap-1.5" data-id="element-1625">
                        <AlertCircle className="w-4 h-4 shrink-0" data-id="element-1626" />
                        No hay zonas de entrega disponibles en este momento.
                      </p> : <>
                        {/* Zone */}
                        <div data-id="element-1627">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1628">
                            Zona <span className="text-red-500" data-id="element-1629">*</span>
                          </label>
                          <select value={selectedMsZone} onChange={e => {
                  setSelectedMsZone(e.target.value);
                  setSelectedMsSubzone('');
                  setSelectedMsFee(0);
                  setSelectedMsCatalogId(null);
                  setErrors(prev => ({
                    ...(prev as any),
                    ms_zone: undefined,
                    ms_subzone: undefined
                  }));
                }} className={cn('w-full h-12 px-3 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none transition-all', (errors as any).ms_zone ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-amber')} data-id="element-1630">
                            <option value="" data-id="element-1631">Selecciona una zona</option>
                            {msZoneNames.map(z => <option key={z} value={z} data-id="element-1632">
                                {z}
                              </option>)}
                          </select>
                          {(errors as any).ms_zone && <p className="text-sm text-red-500 mt-1 flex items-center gap-1.5" data-id="element-1633">
                              <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1634" />
                              {(errors as any).ms_zone}
                            </p>}
                        </div>

                        {/* Subzone */}
                        {selectedMsZone && <div data-field="ms_subzone" data-id="element-1635">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1636">
                              Localidad <span className="text-red-500" data-id="element-1637">*</span>
                            </label>
                            <select value={selectedMsSubzone} onChange={e => {
                  const sub = e.target.value;
                  setSelectedMsSubzone(sub);
                  const match = msSubzonesForZone.find(s => s.subzone === sub);
                  setSelectedMsFee(match?.fee ?? 0);
                  setSelectedMsCatalogId(match?.id ?? null);
                  setErrors(prev => ({
                    ...(prev as any),
                    ms_subzone: undefined
                  }));
                }} className={cn('w-full h-12 px-3 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none transition-all', (errors as any).ms_subzone ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-amber')} data-id="element-1638">
                              <option value="" data-id="element-1639">Selecciona tu localidad</option>
                              {msSubzonesForZone.map(s => <option key={s.subzone} value={s.subzone} data-id="element-1640">
                                  {s.subzone} — ₡{s.fee.toLocaleString('es-CR')}
                                </option>)}
                            </select>
                            {(errors as any).ms_subzone && <p className="text-sm text-red-500 mt-1 flex items-center gap-1.5" data-id="element-1641">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1642" />
                                {(errors as any).ms_subzone}
                              </p>}
                          </div>}

                        {/* Selected fee summary */}
                        {selectedMsZone && selectedMsSubzone && <div className="rounded-xl bg-white border-2 border-emerald-200 p-4 flex items-center justify-between" data-id="element-1643">
                            <div className="flex items-center gap-2 min-w-0" data-id="element-1644">
                              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-1645" />
                              <span className="text-sm text-gray-700 truncate" data-id="element-1646">
                                {selectedMsZone} · {selectedMsSubzone}
                              </span>
                            </div>
                            <span className="font-bold text-brand-amber shrink-0" data-id="element-1647">
                              ₡{selectedMsFee.toLocaleString('es-CR')}
                            </span>
                          </div>}
                      </>}
                  </div>}

                <div data-field="delivery_address" data-id="element-1648">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1649">
                    {t('checkout_address')}
                    {errors.delivery_address && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1650" />}
                  </label>
                  <Textarea label="Dirección exacta" placeholder="Provincia, Cantón, Distrito, señas exactas..." value={formData.delivery_address} onChange={e => handleChange('delivery_address', e.target.value)} error={errors.delivery_address} required data-id="element-1651" />
                </div>

                <div className="space-y-2" data-field="maps_url" data-id="element-1652">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-1.5" data-id="element-1653">
                    Ubicación GPS (Obligatorio)
                    {(errors as any).maps_url && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1654" />}
                  </label>

                  <button type="button" onClick={handleGeolocation} disabled={geoStatus === 'loading'} className={cn('w-full h-12 flex items-center justify-center rounded-xl border-2 border-dashed font-medium transition-all disabled:opacity-70', geoStatus === 'idle' && !(errors as any).maps_url && 'border-gray-200 text-gray-600 hover:border-brand-amber hover:text-brand-amber bg-gray-50', geoStatus === 'idle' && (errors as any).maps_url && 'border-red-300 text-red-600 bg-red-50 animate-pulse', geoStatus === 'loading' && 'border-brand-amber text-brand-amber bg-amber-50/50', geoStatus === 'success' && 'border-emerald-300 text-emerald-700 bg-emerald-50', geoStatus === 'error' && 'border-red-300 text-red-600 bg-red-50')} data-id="element-1655">
                    {geoStatus === 'idle' && <>
                        <Navigation className="w-5 h-5 mr-2" data-id="element-1656" />
                        Usar mi ubicación actual
                      </>}
                    {geoStatus === 'loading' && <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-1657" />
                        Obteniendo ubicación...
                      </>}
                    {geoStatus === 'success' && <>
                        <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-1658" />
                        Ubicación capturada
                      </>}
                    {geoStatus === 'error' && <>
                        <AlertCircle className="w-5 h-5 mr-2" data-id="element-1659" />
                        Reintentar ubicación
                      </>}
                  </button>

                  {(errors as any).maps_url && geoStatus === 'idle' && <p className="text-sm text-red-500 flex items-center gap-1.5 px-1" data-id="element-1660">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1661" />
                      {(errors as any).maps_url}
                    </p>}

                  {geoStatus === 'success' && <div className="flex items-center justify-between px-1" data-id="element-1662">
                      <a href={formData.maps_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center" data-id="element-1663">
                        📍 Ver en el mapa
                      </a>
                      <button type="button" onClick={handleGeolocation} className="text-xs text-gray-500 hover:text-gray-900 underline" data-id="element-1664">
                        Actualizar
                      </button>
                    </div>}

                  {geoStatus === 'error' && <p className="text-xs text-red-500 px-1" data-id="element-1665">{geoError}</p>}
                </div>
              </div>}

            {formData.delivery_type === 'dine_in' && <div data-field="dine_in_arrival_time" className="space-y-2" data-id="element-1666">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-1.5" data-id="element-1667">
                  ¿A qué hora llegarás al local?
                  <span className="text-red-500" data-id="element-1668">*</span>
                  {(errors as any).dine_in_arrival_time && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1669" />}
                </label>
                <input type="time" value={dineInArrivalTime} onChange={e => {
            setDineInArrivalTime(e.target.value);
            if ((errors as any).dine_in_arrival_time) {
              setErrors(prev => ({
                ...(prev as any),
                dine_in_arrival_time: undefined
              }));
            }
          }} className={cn('w-full h-12 px-4 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 transition-all', (errors as any).dine_in_arrival_time ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-primary')} required data-id="element-1670" />
                <p className="text-xs text-gray-500 px-1" data-id="element-1671">
                  Tendremos tu pedido listo para esa hora.
                </p>
                {(errors as any).dine_in_arrival_time && <p className="text-sm text-red-500 px-1 flex items-center gap-1.5" data-id="element-1672">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1673" />
                    {(errors as any).dine_in_arrival_time}
                  </p>}
              </div>}
          </div>
        </div>}

      {/* Payment Method — hidden only for the simplified NatuCafé QR flow */}
      {!useSimplifiedQrCheckout && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1674">
          {isNatucafeMarketplace ? <>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1675">
                <CreditCard className="w-5 h-5 text-brand-primary" data-id="element-1676" />
                Método de pago
              </h3>

              {/* NatuCafé: selector SINPE / Efectivo / Tarjeta */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1677">
                <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'sinpe'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'sinpe' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1678">
                  {formData.payment_method === 'sinpe' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1679" />}
                  <Smartphone className="w-6 h-6 mb-2" data-id="element-1680" />
                  <span className="font-semibold" data-id="element-1681">SINPE Móvil</span>
                </button>

                <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'cash'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'cash' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1682">
                  {formData.payment_method === 'cash' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1683" />}
                  <Banknote className="w-6 h-6 mb-2" data-id="element-1684" />
                  <span className="font-semibold" data-id="element-1685">Efectivo</span>
                </button>

                <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'card'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'card' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1686">
                  {formData.payment_method === 'card' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1687" />}
                  <CreditCard className="w-6 h-6 mb-2" data-id="element-1688" />
                  <span className="font-semibold" data-id="element-1689">Tarjeta</span>
                </button>
              </div>

              {/* Efectivo / Tarjeta = contra entrega con el mensajero.
                        Solo aplica para DELIVERY (con mensajero). En Llevar /
                        Comer en el local no hay mensajero, así que no se muestra. */}
              {formData.delivery_type === 'delivery' && (formData.payment_method === 'cash' || formData.payment_method === 'card') && <div className="mt-5 rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-id="element-1690">
                    <Banknote className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-1691" />
                    <p className="text-sm text-amber-800 leading-relaxed" data-id="element-1692">
                      <span className="font-bold" data-id="element-1693">
                        {formData.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}{' '}
                        contra entrega.
                      </span>{' '}
                      Pagás directamente al mensajero al recibir tu pedido. Si
                      pagás en efectivo, tené listo el monto exacto.
                    </p>
                  </div>}

              {/* SINPE: número + comprobante (solo cuando se elige SINPE) */}
              {formData.payment_method === 'sinpe' && <div className="mt-5" data-field="sinpe_proof" data-id="element-1694">
                  <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 mb-5" data-id="element-1695">
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1" data-id="element-1696">
                      Realizá el SINPE Móvil a este número
                    </p>
                    <p className="text-3xl sm:text-4xl font-black text-emerald-900 tracking-tight tabular-nums mb-1" data-id="element-1697">
                      8314-1666
                    </p>
                    <p className="text-sm text-emerald-800 font-medium" data-id="element-1698">
                      A nombre de {restaurant?.name || 'NatuCafé Coffee Shop'}
                    </p>
                    <p className="text-xs text-emerald-700 mt-3 leading-relaxed" data-id="element-1699">
                      Hacé el SINPE por el monto total del pedido y luego
                      adjuntá la captura del comprobante. Sin la captura no
                      podemos procesar tu pedido.
                    </p>
                  </div>

                  <label className="block text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5" data-id="element-1700">
                    Captura del pago SINPE
                    <span className="text-red-500" data-id="element-1701">*</span>
                    {(errors as any).sinpe_proof && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1702" />}
                  </label>

                  {!sinpeProofFile ? <label className={cn('flex flex-col items-center justify-center w-full h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all', (errors as any).sinpe_proof ? 'border-red-300 bg-red-50 text-red-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-600')} data-id="element-1703">
                      <Upload className="w-8 h-8 mb-2" data-id="element-1704" />
                      <span className="font-semibold text-sm" data-id="element-1705">
                        Tocá para subir captura
                      </span>
                      <span className="text-xs mt-1" data-id="element-1706">
                        JPG, PNG o screenshot
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                setSinpeProofFile(file);
                setSinpeProofPreview(URL.createObjectURL(file));
                setErrors(prev => ({
                  ...(prev as any),
                  sinpe_proof: undefined
                }));
              }
            }} data-id="element-1707" />
                    </label> : <div className="relative rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-3" data-id="element-1708">
                      <img src={sinpeProofPreview} alt="Comprobante SINPE" className="w-full max-h-72 object-contain rounded-lg bg-white" data-id="element-1709" />
                      <div className="flex items-center justify-between mt-3" data-id="element-1710">
                        <div className="flex items-center gap-2 text-sm text-emerald-700 font-semibold min-w-0" data-id="element-1711">
                          <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-1712" />
                          <span className="truncate" data-id="element-1713">
                            {sinpeProofFile.name}
                          </span>
                        </div>
                        <button type="button" onClick={() => {
                setSinpeProofFile(null);
                setSinpeProofPreview('');
              }} className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" aria-label="Quitar captura" data-id="element-1714">
                          <X className="w-4 h-4" data-id="element-1715" />
                        </button>
                      </div>
                    </div>}

                  {(errors as any).sinpe_proof && <p className="text-sm text-red-500 mt-2 flex items-center gap-1.5" data-id="element-1716">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1717" />
                      {(errors as any).sinpe_proof}
                    </p>}

                  {uploadingProof && <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-id="element-1718">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-1719" />
                      Subiendo comprobante...
                    </p>}

                  <p className="text-xs text-gray-500 mt-3" data-id="element-1720">
                    Sin la captura no podemos procesar tu pedido.
                  </p>
                </div>}
            </> : <>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1721">
                <CreditCard className="w-5 h-5 text-brand-primary" data-id="element-1722" />
                {t('checkout_payment')}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1723">
                {restaurant?.accepts_cash && <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'cash'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'cash' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1724">
                    {formData.payment_method === 'cash' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1725" />}
                    <Banknote className="w-6 h-6 mb-2" data-id="element-1726" />
                    <span className="font-semibold" data-id="element-1727">{t('checkout_cash')}</span>
                  </button>}

                {restaurant?.accepts_card && !(isCrackPollo && formData.delivery_type === 'delivery') && <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'card'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'card' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1728">
                      {formData.payment_method === 'card' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1729" />}
                      <CreditCard className="w-6 h-6 mb-2" data-id="element-1730" />
                      <span className="font-semibold" data-id="element-1731">
                        {t('checkout_card')}
                      </span>
                    </button>}

                {restaurant?.accepts_sinpe && <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'sinpe'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'sinpe' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1732">
                    {formData.payment_method === 'sinpe' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1733" />}
                    <Smartphone className="w-6 h-6 mb-2" data-id="element-1734" />
                    <span className="font-semibold" data-id="element-1735">{t('checkout_sinpe')}</span>
                  </button>}
              </div>

              {/* Crack Pollo & Cabaña Parrillera (marketplace): Cash amount input */}
              {(isCrackPollo || isCabanaParrillera && !isQrTableForThisRestaurant) && formData.payment_method === 'cash' && <div className="mt-6 p-5 rounded-2xl border border-brand-amber/30 bg-brand-amber/5" data-id="element-1736">
                    <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-1737">
                      ¿Con cuánto vas a pagar?
                    </label>
                    <div className="relative" data-id="element-1738">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-1739">
                        ₡
                      </span>
                      <Input type="number" placeholder="Ej. 10000" value={cashAmount} onChange={e => setCashAmount(e.target.value)} className="pl-8" data-id="element-1740" />
                    </div>
                    {cashAmount && Number(cashAmount) > 0 && <div className="mt-3 text-sm flex items-center justify-between bg-white p-3 rounded-xl border border-gray-100" data-id="element-1741">
                        <span className="text-gray-600" data-id="element-1742">
                          Vuelto a entregar:
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-1743">
                          {Number(cashAmount) >= orderTotal ? `₡${(Number(cashAmount) - orderTotal).toLocaleString('es-CR')}` : 'Monto insuficiente'}
                        </span>
                      </div>}
                  </div>}

              {/* SINPE proof upload — for any restaurant accepting SINPE */}
              {formData.payment_method === 'sinpe' && <div className="mt-6" data-field="sinpe_proof" data-id="element-1744">
                  {restaurant?.whatsapp_number ? <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 mb-4" data-id="element-1745">
                      <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1" data-id="element-1746">
                        Realizá el SINPE Móvil a este número
                      </p>
                      <p className="text-3xl sm:text-4xl font-black text-emerald-900 tracking-tight tabular-nums mb-1" data-id="element-1747">
                        {String(restaurant.whatsapp_number).replace(/\D/g, '').replace(/^506/, '').replace(/^(\d{4})(\d{4}).*/, '$1-$2')}
                      </p>
                      {restaurant?.name && <p className="text-sm text-emerald-800 font-medium" data-id="element-1748">
                          A nombre de {restaurant.name}
                        </p>}
                      <p className="text-xs text-emerald-700 mt-3 leading-relaxed" data-id="element-1749">
                        Hacé el SINPE por el monto total del pedido y adjuntá la
                        captura del comprobante. Sin la captura no podemos
                        procesar tu pedido.
                      </p>
                    </div> : <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4 mb-4" data-id="element-1750">
                      <p className="text-sm text-emerald-800 leading-relaxed" data-id="element-1751">
                        Hacé el SINPE Móvil al número del restaurante por el
                        monto total del pedido y adjuntá la captura del
                        comprobante. Sin la captura no podemos procesar tu
                        pedido.
                      </p>
                    </div>}

                  <label className="block text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5" data-id="element-1752">
                    Captura del pago SINPE
                    <span className="text-red-500" data-id="element-1753">*</span>
                    {(errors as any).sinpe_proof && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1754" />}
                  </label>

                  {!sinpeProofFile ? <label className={cn('flex flex-col items-center justify-center w-full h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all', (errors as any).sinpe_proof ? 'border-red-300 bg-red-50 text-red-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-600')} data-id="element-1755">
                      <Upload className="w-8 h-8 mb-2" data-id="element-1756" />
                      <span className="font-semibold text-sm" data-id="element-1757">
                        Tocá para subir captura
                      </span>
                      <span className="text-xs mt-1" data-id="element-1758">
                        JPG, PNG o screenshot
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                setSinpeProofFile(file);
                setSinpeProofPreview(URL.createObjectURL(file));
                setErrors(prev => ({
                  ...(prev as any),
                  sinpe_proof: undefined
                }));
              }
            }} data-id="element-1759" />
                    </label> : <div className="relative rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-3" data-id="element-1760">
                      <img src={sinpeProofPreview} alt="Comprobante SINPE" className="w-full max-h-72 object-contain rounded-lg bg-white" data-id="element-1761" />
                      <div className="flex items-center justify-between mt-3" data-id="element-1762">
                        <div className="flex items-center gap-2 text-sm text-emerald-700 font-semibold min-w-0" data-id="element-1763">
                          <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-1764" />
                          <span className="truncate" data-id="element-1765">
                            {sinpeProofFile.name}
                          </span>
                        </div>
                        <button type="button" onClick={() => {
                setSinpeProofFile(null);
                setSinpeProofPreview('');
              }} className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" aria-label="Quitar captura" data-id="element-1766">
                          <X className="w-4 h-4" data-id="element-1767" />
                        </button>
                      </div>
                    </div>}

                  {(errors as any).sinpe_proof && <p className="text-sm text-red-500 mt-2 flex items-center gap-1.5" data-id="element-1768">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1769" />
                      {(errors as any).sinpe_proof}
                    </p>}

                  {uploadingProof && <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-id="element-1770">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-1771" />
                      Subiendo comprobante...
                    </p>}
                </div>}
            </>}
        </div>}

      {/* Notes — hidden only for the simplified NatuCafé QR flow */}
      {!useSimplifiedQrCheckout && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1772">
          <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-1773">
            {t('checkout_notes')}
          </label>
          <Textarea value={formData.notes} onChange={e => setFormData(prev => ({
        ...prev,
        notes: e.target.value
      }))} placeholder={t('checkout_notes_placeholder')} className="resize-none h-24" data-id="element-1774" />
        </div>}

      {/* Invoice Request — Hidden from the customer-facing checkout. Other
                                            admin/system flows that handle invoice requests are unaffected. */}
      {false && Number(restaurant?.id) === 19 && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1775">
          <div className="flex items-start justify-between gap-4 mb-4" data-id="element-1776">
            <div data-id="element-1777">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-1778">
                <Mail className="w-5 h-5 text-brand-primary" data-id="element-1779" />
                ¿Necesitas factura?
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1780">
                Opcional. Si la necesitas, el restaurante te la enviará por
                correo.
              </p>
            </div>
            <div className="flex gap-2 shrink-0" data-id="element-1781">
              <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            wants_invoice: true
          }))} className={cn('px-5 py-2 rounded-xl border-2 font-semibold text-sm transition-all', formData.wants_invoice ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-gray-200 text-gray-500 hover:border-gray-300')} data-id="element-1782">
                Sí
              </button>
              <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            wants_invoice: false,
            invoice_customer_name: '',
            invoice_legal_id: '',
            invoice_email: ''
          }))} className={cn('px-5 py-2 rounded-xl border-2 font-semibold text-sm transition-all', !formData.wants_invoice ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-gray-200 text-gray-500 hover:border-gray-300')} data-id="element-1783">
                No
              </button>
            </div>
          </div>

          {formData.wants_invoice && <div className="space-y-4 pt-2 border-t border-gray-100" data-id="element-1784">
              <div data-field="invoice_customer_name" data-id="element-1785">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1786">
                  Nombre o razón social <span className="text-red-500" data-id="element-1787">*</span>
                </label>
                <Input value={formData.invoice_customer_name || ''} onChange={e => handleChange('invoice_customer_name' as any, e.target.value)} error={(errors as any).invoice_customer_name} placeholder="Nombre completo o empresa" required data-id="element-1788" />
              </div>
              <div data-field="invoice_legal_id" data-id="element-1789">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1790">
                  Cédula / ID fiscal{' '}
                  <span className="text-gray-400" data-id="element-1791">(opcional)</span>
                </label>
                <Input value={formData.invoice_legal_id || ''} onChange={e => handleChange('invoice_legal_id' as any, e.target.value)} placeholder="1-2345-6789" data-id="element-1792" />
              </div>
              <div data-field="invoice_email" data-id="element-1793">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1794">
                  Correo para enviar la factura{' '}
                  <span className="text-red-500" data-id="element-1795">*</span>
                </label>
                <Input type="email" value={formData.invoice_email || ''} onChange={e => handleChange('invoice_email' as any, e.target.value)} error={(errors as any).invoice_email} placeholder="email@ejemplo.com" required data-id="element-1796" />
              </div>
            </div>}
        </div>}

      {/* Terms and Conditions — hidden for returning customers */}
      {!isReturningCustomer && <div className="flex items-start gap-3 px-2" data-id="element-1797">
          <div className="flex items-center h-5" data-id="element-1798">
            <input id="terms" type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-brand-primary focus:ring-brand-primary" data-id="element-1799" />
          </div>
          <div className="text-sm" data-id="element-1800">
            <label htmlFor="terms" className="font-medium text-gray-900" data-id="element-1801">
              {t('checkout_terms')}
            </label>
            <p className="text-gray-600" data-id="element-1802">{t('checkout_terms')}</p>
          </div>
        </div>}

      <Button type="submit" className="w-full h-14 text-lg font-bold" disabled={isLoading || !termsAccepted} data-id="element-1803">
        {isLoading ? <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-1804" />
            {t('checkout_placing')}
          </> : t('checkout_place_order')}
      </Button>

      {/* Terms Modal */}
      {showTermsModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowTermsModal(false)} data-id="element-1805">
          <div className="relative w-full max-w-lg max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1806">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1807">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-1808">
                Términos y Condiciones
              </h2>
              <button onClick={() => setShowTermsModal(false)} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-1809">
                <AlertCircle className="w-5 h-5" data-id="element-1810" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 text-sm text-gray-600 leading-relaxed space-y-4" data-id="element-1811">
              <h3 className="font-bold text-gray-900" data-id="element-1812">
                1. Aceptación de los Términos
              </h3>
              <p data-id="element-1813">
                Al realizar un pedido a través de NOIZA, usted acepta estos
                términos y condiciones en su totalidad. Si no está de acuerdo
                con alguna parte, no debe utilizar nuestro servicio.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-1814">
                2. Servicio de Pedidos
              </h3>
              <p data-id="element-1815">
                NOIZA actúa como intermediario tecnológico entre el cliente y el
                restaurante. Los pedidos se procesan y confirman directamente
                con el restaurante a través de WhatsApp.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-1816">3. Precios y Pagos</h3>
              <p data-id="element-1817">
                Los precios mostrados incluyen impuestos. El pago se realiza
                directamente al restaurante según el método seleccionado
                (efectivo, SINPE Móvil o tarjeta). NOIZA no procesa pagos.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-1818">4. Entregas</h3>
              <p data-id="element-1819">
                Los tiempos de entrega son estimados y pueden variar según la
                demanda. El costo de envío se muestra antes de confirmar el
                pedido. El restaurante es responsable de la entrega.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-1820">5. Cancelaciones</h3>
              <p data-id="element-1821">
                Las cancelaciones deben coordinarse directamente con el
                restaurante a través de WhatsApp. Una vez que el pedido está en
                preparación, no se garantiza la cancelación.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-1822">6. Datos Personales</h3>
              <p data-id="element-1823">
                Recopilamos nombre, teléfono y ubicación únicamente para
                procesar su pedido. No compartimos sus datos con terceros más
                allá del restaurante seleccionado. Su información se maneja
                conforme a la Ley de Protección de Datos de Costa Rica.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-1824">7. Responsabilidad</h3>
              <p data-id="element-1825">
                NOIZA no es responsable por la calidad de los alimentos, tiempos
                de entrega o cualquier inconveniente con el restaurante.
                Cualquier reclamo debe dirigirse directamente al
                establecimiento.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-1826">8. Modificaciones</h3>
              <p data-id="element-1827">
                Nos reservamos el derecho de modificar estos términos en
                cualquier momento. Los cambios serán efectivos al publicarse en
                la plataforma.
              </p>
              <p className="text-xs text-gray-400 pt-4 border-t border-gray-100" data-id="element-1828">
                Última actualización: Marzo 2026 · Los Santos, Costa Rica
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 shrink-0" data-id="element-1829">
              <button onClick={() => {
            setTermsAccepted(true);
            setShowTermsModal(false);
          }} className="w-full py-3 rounded-xl bg-brand-amber text-white font-bold text-sm hover:bg-amber-600 transition-colors" data-id="element-1830">
                Aceptar Términos y Condiciones
              </button>
            </div>
          </div>
        </div>}

      {/* ─── La Cabaña Parrillera — Express fee notice ─── */}
      {showCabanaExpressNotice && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowCabanaExpressNotice(false)} data-id="element-1831">
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-1832">
            <div className="px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #F59E0B 100%)'
        }} data-id="element-1833">
              <div className="text-4xl mb-1" data-id="element-1834">🔥 🍔 🛖</div>
              <h3 className="text-white font-black text-lg tracking-tight" data-id="element-1835">
                Servicio Express
              </h3>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-1836">
              <p className="text-[15px] text-gray-700 leading-relaxed text-center" data-id="element-1837">
                El monto del <strong data-id="element-1838">express</strong> no está incluido en el
                precio.
              </p>
              <p className="text-[14px] text-gray-600 leading-relaxed text-center" data-id="element-1839">
                Por favor consulte por el costo del express en{' '}
                <strong data-id="element-1840">WhatsApp</strong>.
              </p>
              <p className="text-[14px] font-semibold text-[#FF6B35] text-center pt-1" data-id="element-1841">
                ¡Muchas gracias!
              </p>
            </div>
            <div className="px-6 pb-6" data-id="element-1842">
              <button type="button" onClick={() => setShowCabanaExpressNotice(false)} className="w-full py-3 rounded-2xl bg-[#FF6B35] hover:bg-[#FF5722] active:scale-[0.98] text-white font-bold text-sm uppercase tracking-wider transition-all shadow-lg shadow-[#FF6B35]/30" data-id="element-1843">
                Entendido
              </button>
            </div>
          </div>
        </div>}
    </form>;
}